import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useJobStore, Job } from "../../../store/jobStore";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { countJobsByStatus, getBadgeColor } from "../helpers";
import { FilterType, JobCounts, BadgeColor } from "../types";
import { getAwaitingInputRoute } from "../../../features/hitl/presentPlan";
import { useHitlEnabled } from "../../../hooks/useHitlEnabled";

export const useJobMonitorState = () => {
  const [open, setOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isPulsating, setIsPulsating] = useState(false);
  const [retryingJob, setRetryingJob] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [newJobIds, setNewJobIds] = useState<Set<string>>(new Set());
  const [completedJobIds, setCompletedJobIds] = useState<Set<string>>(new Set());
  const [showCompletionToast, setShowCompletionToast] = useState(false);

  const prevActiveJobsCountRef = useRef(0);
  const prevJobStatusesRef = useRef<Record<string, ReportJobStatus>>({});

  const jobs = useJobStore((state) => state.jobs);
  const removeJob = useJobStore((state) => state.removeJob);
  const retrySingleJob = useJobStore((state) => state.retrySingleJob);
  const activeJobsCount = useJobStore((state) => state.activeJobsCount());
  const hitlEnabled = useHitlEnabled();

  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const jobCounts: JobCounts = useMemo(() => countJobsByStatus(jobs), [jobs]);

  const filteredJobs = useMemo(() => {
    switch (activeFilter) {
      case "in_progress":
        return jobs.filter(
          (j) =>
            j.status === ReportJobStatus.PENDING ||
            j.status === ReportJobStatus.STARTED ||
            j.status === ReportJobStatus.GENERATING ||
            j.status === ReportJobStatus.EXTRACTING
        );
      case "completed":
        return jobs.filter((j) => j.status === ReportJobStatus.PENDING_REVIEW);
      case "awaiting_input":
        return jobs.filter((j) => j.status === ReportJobStatus.AWAITING_INPUT);
      case "failed":
        return jobs.filter((j) => j.status === ReportJobStatus.FAILED);
      default:
        return jobs;
    }
  }, [jobs, activeFilter]);

  const badgeColor: BadgeColor = useMemo(() => getBadgeColor(jobCounts), [jobCounts]);

  useEffect(() => {
    let pulseTimer: NodeJS.Timeout | undefined;

    if (activeJobsCount > prevActiveJobsCountRef.current) {
      setToastMessage("New report is being generated. Check your Report Tracker.");
      setShowToast(true);
      setIsPulsating(true);

      jobs.forEach((job) => {
        if (job.status === ReportJobStatus.PENDING) {
          setNewJobIds((prev) => new Set(prev).add(job.id));
        }
      });

      pulseTimer = setTimeout(() => {
        setIsPulsating(false);
        setNewJobIds(new Set());
      }, 5000);
    }

    jobs.forEach((job) => {
      const prevStatus = prevJobStatusesRef.current[job.id];
      if (prevStatus === ReportJobStatus.PENDING && job.status === ReportJobStatus.PENDING_REVIEW) {
        setCompletedJobIds((prev) => new Set(prev).add(job.id));
        setShowCompletionToast(true);

        setTimeout(() => {
          setCompletedJobIds((prev) => {
            const next = new Set(prev);
            next.delete(job.id);
            return next;
          });
        }, 2000);
      }
    });

    prevActiveJobsCountRef.current = activeJobsCount;
    prevJobStatusesRef.current = jobs.reduce(
      (acc, job) => ({ ...acc, [job.id]: job.status }),
      {} as Record<string, ReportJobStatus>
    );

    return () => {
      if (pulseTimer) clearTimeout(pulseTimer);
    };
  }, [activeJobsCount, jobs]);

  useEffect(() => {
    const store = useJobStore.getState();
    store.jobs.forEach((job) => {
      if (
        job.status === ReportJobStatus.PENDING ||
        job.status === ReportJobStatus.AWAITING_INPUT
      ) {
        store.startPollingJob(job.id);
      }
    });

    return () => {
      const currentStore = useJobStore.getState();
      currentStore.jobs.forEach((job) => {
        currentStore.stopPollingJob(job.id);
      });
    };
  }, []);

  const toggleDrawer = useCallback(() => setOpen((prev) => !prev), []);

  const handleRemoveJob = useCallback(
    (job: Job, event: React.MouseEvent) => {
      event.stopPropagation();
      removeJob(job.id);
    },
    [removeJob]
  );

  const handleRetryJob = useCallback(
    async (job: Job, event: React.MouseEvent) => {
      event.stopPropagation();
      setRetryingJob(true);
      await retrySingleJob(job);
      setRetryingJob(false);
    },
    [retrySingleJob]
  );

  const handleJobClick = useCallback(
    (job: Job) => {
      if (job.status === ReportJobStatus.PENDING_REVIEW && job.id && job.reportId) {
        setOpen(false);
        navigate(`/edit-section/${job.reportId}`, {
          state: { sparseInfo: job.sparseInfo, fromJobMonitor: true },
        });
        return;
      }

      if (job.status === ReportJobStatus.AWAITING_INPUT) {
        setOpen(false);
        navigate(getAwaitingInputRoute(job.reportId || job.id, hitlEnabled));
      }
    },
    [hitlEnabled, navigate]
  );

  const handleCloseToast = useCallback((_: React.SyntheticEvent | Event | undefined, reason?: string) => {
    if (reason === "clickaway") return;
    setShowToast(false);
  }, []);

  return {
    // UI state
    open,
    toggleDrawer,
    showToast,
    toastMessage,
    handleCloseToast,
    isPulsating,
    badgeColor,
    showCompletionToast,
    setShowCompletionToast,
    retryingJob,

    // filtering
    activeFilter,
    setActiveFilter,
    jobCounts,
    filteredJobs,

    // ids
    newJobIds,
    completedJobIds,

    // handlers
    handleRemoveJob,
    handleRetryJob,
    handleJobClick,

    // layout
    isSmallScreen,
    isMobile,
    theme,
    jobs,
    activeJobsCount,
  };
};
